import { PromptAnalyticsResponse } from '../../../../apis/promptAnalyticsApi';
import { SummaryCardData } from '../SummaryCard/types';
import { PromptData } from '../PromptList/types';
import { UserPromptListProps } from '../PromptList/UserPromptList';

export const transformAnalyticsData = (data: PromptAnalyticsResponse) => {
  // Transform summary cards data
  const summaryData: SummaryCardData[] = [
    {
      id: 'total-prompts',
      title: 'Total prompts',
      value: (data.total_prompts ?? 0).toString(),
      subtitle: '',
    },
    {
      id: 'active-users',
      title: 'Active users',
      value: (data.unique_users ?? 0).toString(),
      subtitle: '',
    },
    {
      id: 'top-category',
      title: 'Top category',
      value: data.categories?.length > 0 ? data.categories[0].category : 'N/A',
      subtitle: data.categories?.length > 0 ? `${data.categories[0].prompt_count ?? 0} prompts` : '',
    },
    {
      id: 'most-active-user',
      title: 'Most active user',
      value: data.most_active_users?.length > 0 ? formatUserName(data.most_active_users[0].email) : 'N/A',
      subtitle: data.most_active_users?.length > 0 ? `${data.most_active_users[0].prompt_count ?? 0} prompts` : '',
    },
  ];

  // Transform prompt data for "By Similar prompts" view
  const promptData: PromptData[] = (data.categories ?? []).flatMap((category) => {
    // Prefer optimization_suggestions as the primary source, fallback to consolidation_suggestions
    const hasOptimizations = (category.optimization_suggestions ?? []).length > 0;
    const hasConsolidations = (category.consolidation_suggestions ?? []).length > 0;
    
    if (hasOptimizations) {
      // Use optimization suggestions
      return (category.optimization_suggestions ?? []).map((optimization, index) => {
        // Try to find usage count from most_used_prompts by matching original_prompt
        const usageInfo = (data.most_used_prompts ?? []).find(
          p => p.prompt_text === optimization.original_prompt
        );
        
        return {
          id: `${category.category}-${index}`,
          title: optimization.original_prompt,
          similarCount: usageInfo?.usage_count ?? 1,
          usedByCount: usageInfo?.unique_users ?? category.unique_users ?? 0,
          users: (data.most_active_users ?? []).slice(0, 5).map((user, idx) => ({
            id: `user-${idx}`,
            initials: getInitials(user.email ?? ''),
          })),
          suggestedPrompt: optimization.optimized_prompt,
          relatedPrompt: (category.library_prompt_matches ?? []).length > 0 
            ? (typeof category.library_prompt_matches[0] === 'string' 
              ? category.library_prompt_matches[0] 
              : '') 
            : '',
          showRelatedFromLibrary: (category.library_prompt_matches ?? []).length > 0,
        };
      });
    } else if (hasConsolidations) {
      // Fallback to consolidation suggestions
      return (category.consolidation_suggestions ?? []).map((consolidation, index) => {
        // Try to find corresponding optimization
        const optimization = (category.optimization_suggestions ?? []).find(
          opt => opt.original_prompt === consolidation.consolidated_prompt
        );
        
        return {
          id: `${category.category}-${index}`,
          title: consolidation.consolidated_prompt ?? '',
          similarCount: consolidation.estimated_usage ?? 0,
          usedByCount: category.unique_users ?? 0,
          users: (data.most_active_users ?? []).slice(0, 5).map((user, idx) => ({
            id: `user-${idx}`,
            initials: getInitials(user.email ?? ''),
          })),
          suggestedPrompt: optimization?.optimized_prompt ?? consolidation.consolidated_prompt ?? '',
          relatedPrompt: (category.library_prompt_matches ?? []).length > 0 
            ? (typeof category.library_prompt_matches[0] === 'string' 
              ? category.library_prompt_matches[0] 
              : '') 
            : '',
          showRelatedFromLibrary: (category.library_prompt_matches ?? []).length > 0,
        };
      });
    } else {
      // No suggestions available, return empty array
      return [];
    }
  });

  // Transform user prompt data for "By user" view
  const userPromptData: UserPromptListProps = {
    totalCount: data.total_prompts ?? 0,
    domain: extractDomainName(data.domain_id ?? ''),
    subDomain: 'All Sub-domains',
    persona: 'All Personas',
    mostFrequentPrompts: (data.most_used_prompts ?? []).slice(0, 5).map(p => p.prompt_text ?? ''),
    users: (data.most_active_users ?? []).map((user, index) => ({
      id: `user-${index}`,
      name: formatUserName(user.email ?? ''),
      email: user.email ?? '',
      initials: getInitials(user.email ?? ''),
      date: formatDate(user.last_activity ?? new Date().toISOString()),
      product: extractDomainName(data.domain_id ?? ''),
      prompts: (user.most_used_prompts ?? []).slice(0, 7), // Limit to 7 prompts as shown in design
    })),
  };

  return { summaryData, promptData, userPromptData };
};

const formatUserName = (email: string): string => {
  if (!email) return 'Unknown User';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  return parts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};

const getInitials = (email: string): string => {
  if (!email) return 'UU';
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const extractDomainName = (domainId: string): string => {
  if (!domainId) return 'Unknown';
  // Extract readable domain name from domain_id like "ellisai_07ecfbe3632146f99698d5915b2871c5"
  const parts = domainId.split('_');
  if (parts.length > 0) {
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  }
  return domainId;
};

const formatDate = (isoDate: string): string => {
  if (!isoDate) return 'N/A';
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  } catch {
    return 'N/A';
  }
};
